/** 
 * DASHBOARD PRINCIPAL - GROWLY (COM SNACKBARS COMPLETOS)
 * Área logada do usuário
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import './dashboard.css';
import api from '../../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [currentUsage, setCurrentUsage] = useState({
    campanhas: 0,
    estrategias: 0
  });
  const [planLimits, setPlanLimits] = useState({
    campanhas: 5,
    estrategias: 10
  });

  // 🎉 ESTADOS PARA SNACKBARS
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 🎉 FUNÇÃO PARA MOSTRAR SNACKBAR
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  // 🎉 FUNÇÃO PARA FECHAR SNACKBAR
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // ⭐ FUNÇÃO DE CARREGAMENTO DE CAMPANHAS
  const loadCampaigns = useCallback(async () => {
    try {
      console.log('📡 Carregando campanhas...');

      const response = await api.get('/campaigns');
      console.log('📊 Resposta campanhas:', response.data);

      if (response.data?.success && Array.isArray(response.data.campaigns)) {
        setCampaigns(response.data.campaigns);
        console.log('✅ Campanhas carregadas:', response.data.campaigns.length);

        if (response.data.campaigns.length > 0) {
          showSnackbar('Campanhas carregadas com sucesso!', 'success');
        }

      } else if (Array.isArray(response.data)) {
        setCampaigns(response.data);
      } else {
        setCampaigns([]);
      }

    } catch (error) {
      console.error('❌ Erro ao carregar campanhas:', error);
      setCampaigns([]);

      if (error.response?.status === 404) {
        console.log('ℹ️ Nenhuma campanha encontrada (404)');
        return;
      }

      showSnackbar('Erro ao carregar campanhas', 'error');
    }
  }, [showSnackbar]);

  // ⭐ FUNÇÃO DE ASSINATURA
  const loadSubscription = useCallback(async () => {
    try {
      console.log('📡 Carregando assinatura...');
      const response = await api.get('/payments/subscription');

      if (response.data?.success && response.data.subscription) {
        setSubscription(response.data.subscription);

        let limits = { campanhas: 5, estrategias: 10 };

        if (response.data.subscription.planDetails?.limits) {
          limits = response.data.subscription.planDetails.limits;
        }

        setPlanLimits(limits);

      } else {
        console.log('⚠️ Nenhuma assinatura encontrada - plano free');
        setSubscription(null);
      }

    } catch (error) {
      console.error('❌ Erro ao carregar assinatura:', error);
      setSubscription(null);
      showSnackbar('Erro ao carregar assinatura', 'warning');
    }
  }, [showSnackbar]);

  // ⭐ FUNÇÃO DE USO (AGORA NÃO QUEBRA)
  const loadUsage = useCallback(async () => {
    try {
      console.log('📡 Carregando uso...');
      const response = await api.get('/usage');

      if (response.data?.success) {
        setCurrentUsage({
          campanhas: response.data.usage?.campanhas_criadas || 0,
          estrategias: response.data.usage?.estrategias_criadas || 0
        });

        if (response.data.limits) {
          setPlanLimits(response.data.limits);
        }
      }

    } catch (error) {
      console.error('❌ Erro ao carregar uso:', error);

      if (error.response?.status === 404) {
        console.log('ℹ️ Endpoint /usage não existe no back-end. Ignorando...');
        return;
      }

      showSnackbar('Erro ao carregar uso', 'warning');
    }
  }, [showSnackbar]);

  // ⭐ FUNÇÃO PRINCIPAL (AGORA SEM loadUsage)
  const loadAllData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await Promise.all([
        loadCampaigns(),
        loadSubscription()
        // ❌ loadUsage() REMOVIDO POR ENQUANTO
      ]);

      showSnackbar('Dados carregados com sucesso!', 'success');

    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do dashboard');
      showSnackbar('Erro ao carregar dados do dashboard', 'error');

    } finally {
      setLoading(false);
    }
  }, [navigate, loadCampaigns, loadSubscription, showSnackbar]);

  // ⭐ useEffect PRINCIPAL
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setUser(userData ? JSON.parse(userData) : null);
    } catch {
      setUser(null);
    }

    loadAllData();

    const handleFocus = () => loadAllData();
    const handleVisibilityChange = () => {
      if (!document.hidden) loadAllData();
    };

    const handleCampaignCreated = () => {
      loadAllData();
      showSnackbar('Campanha criada com sucesso!', 'success');
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('campaignCreated', handleCampaignCreated);

    window.dashboardRefresh = loadAllData;

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('campaignCreated', handleCampaignCreated);
      delete window.dashboardRefresh;
    };

  }, [navigate, loadAllData, showSnackbar]);

  // ⭐ FORMATADORES E HANDLERS
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    showSnackbar('Logout realizado com sucesso', 'info');
    setTimeout(() => navigate('/login'), 1000);
  };

  const getUsagePercentage = (current, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const handleNavigateToStrategy = () => {
    if (currentUsage.campanhas >= planLimits.campanhas) {
      showSnackbar('Limite de campanhas atingido. Faça upgrade do seu plano.', 'warning');
      return;
    }
    showSnackbar('Redirecionando...', 'info');
    setTimeout(() => navigate('/strategy'), 300);
  };

  const handleConnectGoogleAds = async () => {
    try {
      showSnackbar('Conectando ao Google Ads...', 'info');

      const response = await api.get('/google-ads/authorize');

      if (response.data.mode === 'simulation' && response.data.autoConnected) {
        showSnackbar('Modo simulação ativado!', 'success');
        return;
      }

      if (response.data.authUrl) {
        showSnackbar('Redirecionando ao Google...', 'info');
        setTimeout(() => (window.location.href = response.data.authUrl), 800);
      }

    } catch (error) {
      console.error('Erro ao conectar Google Ads:', error);
      showSnackbar('Erro ao conectar Google Ads', 'error');
    }
  };

  const isSubscriptionActive = () => {
    if (!subscription) return false;

    return (
      subscription.status === 'active' &&
      subscription.plan_name &&
      subscription.plan_name !== 'free'
    );
  };

  if (loading || !user) {
    return (
      <div className="dashboard">
        <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* 🎉 SNACKBARS */}
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert severity="success">Dados carregados com sucesso!</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {/* HEADER */}
      <header className="dashboard-header">
        <h1>Bem-vindo, {user.name}! 👋</h1>
        <button onClick={handleLogout} className="btn-logout">Sair</button>
      </header>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="dashboard-content">
        {/* ESTATÍSTICAS */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Campanhas Ativas</h3>
            <p className="stat-value">{campaigns.length}</p>
          </div>
          <div className="stat-card">
            <h3>ROI Médio</h3>
            <p className="stat-value">+287%</p>
          </div>
          <div className="stat-card">
            <h3>Custo por Lead</h3>
            <p className="stat-value">R$ 12,50</p>
          </div>
        </div>

        {/* ASSINATURA */}
        <div className="subscription-section">
          <h3>📊 Minha Assinatura</h3>

          {isSubscriptionActive() ? (
            <div>
              <p><strong>Plano atual:</strong> {subscription.planDetails?.name || subscription.plan_name}</p>
              <p><strong>Status:</strong> {subscription.status}</p>
              <p><strong>Próximo pagamento:</strong> {formatDate(subscription.current_period_end)}</p>
            </div>
          ) : (
            <div>
              <p>🚀 Teste grátis ativo</p>
              <button onClick={() => navigate('/pricing')}>Upgrade</button>
            </div>
          )}
        </div>

        {/* AÇÕES */}
        <div className="quick-actions">
          <h2>Criar Nova Campanha</h2>
          <button onClick={handleNavigateToStrategy} className="btn-create">
            🧠 Gerar Estratégia com IA
          </button>
        </div>

        {/* INTEGRAÇÕES */}
        <div className="integrations-section">
          <h3>🔗 Integrações</h3>

          <div className="integration-card">
            <div className="integration-info">
              <h4>Google Ads</h4>
              <p>Conectar com Google Ads</p>
            </div>
            <button onClick={handleConnectGoogleAds} className="btn-integration">
              Conectar
            </button>
          </div>
        </div>

        {/* CAMPANHAS */}
        <div className="campaigns-list">
          <h2>Suas Campanhas</h2>

          {campaigns.length === 0 ? (
            <p>Nenhuma campanha ainda. Crie sua primeira!</p>
          ) : (
            <div className="campaigns-grid">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="campaign-item">
                  <h3>{campaign.name}</h3>
                  {campaign.status && <p>Status: {campaign.status}</p>}
                  {campaign.budget && <p>Orçamento: R$ {campaign.budget}</p>}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
